/**
 * The open history download behind the /looker filter-bar pill. Runs for every
 * workspace view, whatever the template holds; fetches nothing in the master's
 * all-workspaces scope. Same run-key rules as the app boxes (template + scope +
 * period + language), and a stale result is never shown.
 *
 * @module hooks/useLookerBackfill
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadBackfill } from '@/services/looker/lookerAppLoaders';
import type { BackfillSummary } from '@/services/looker/lookerAppData';
import type { LookerDashboardPayload } from '@/services/looker/lookerTypes';
import { logError } from '@/services/logger';
import { payloadRunKey } from './useLookerAppTiles';

export type BackfillState =
  /** No payload, or the all-workspaces scope: nothing to say, nothing fetched. */
  | { status: 'idle' }
  | { status: 'loading' }
  /** platforms [] = no open backfill; eta is a lower bound for the whole window. */
  | ({ status: 'ready' } & BackfillSummary)
  /** The read failed (e.g. a master who is not a member): never shown as "none". */
  | { status: 'failed' };

type Settled = Exclude<BackfillState, { status: 'idle' } | { status: 'loading' }>;

export const useLookerBackfill = (payload: LookerDashboardPayload | null): BackfillState => {
  const { i18n } = useTranslation();
  const workspaceId = payload?.scope.kind === 'workspace' ? payload.scope.workspaceId : null;
  const runKey = payload && workspaceId ? payloadRunKey(payload, i18n.language) : '';
  const [result, setResult] = useState<{ key: string; state: Settled } | null>(null);

  useEffect(() => {
    if (!runKey || !workspaceId) return;
    let ignore = false;
    loadBackfill(workspaceId, new Date()).then(
      (summary) => {
        if (!ignore) setResult({ key: runKey, state: { status: 'ready', ...summary } });
      },
      (err: unknown) => {
        logError('LookerBackfill', 'backfill read failed', err);
        if (!ignore) setResult({ key: runKey, state: { status: 'failed' } });
      },
    );
    return () => {
      ignore = true;
    };
  }, [runKey, workspaceId]);

  return useMemo<BackfillState>(() => {
    if (!runKey) return { status: 'idle' };
    return result && result.key === runKey ? result.state : { status: 'loading' };
  }, [runKey, result]);
};
